import { Injectable } from '@angular/core';
import { DateTime } from '@mode-switch/express';
import { BasicChannel, ColorTarget, Encoding, EncodingResult, MarkType, VariableType } from '@mode-vegas/vegas';
import { ChartFacade, DragInfo, SelectedChartService } from '@mode/charts/contract';
import { ChartTypes, DataProfilingTypes } from '@mode/shared/contract-common';
import { CompiledField, CompiledFormula } from '@mode/shared/util-switch';
import { PlanMetadataFacade } from '@mode/switch-views/contract';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { isPresent } from '@mode/shared/util-js';
import * as ChartSwitchActions from '../chart/chart-switch.actions';
import { DomainAppState } from '../charts-domain.types';

/**
 * Stub facade to support the viz-designer action dispatching for effect persistence.
 * For the time being, this is taking the place of an individual service used in the
 * legacy flow. We may build more explicit abstractions around this moving forward to
 * generalize things further where applicable.
 *
 * This may be extended to also generate observables in the future.
 */

@Injectable()
export class VizDesignerFacade implements SelectedChartService {
  private _dragField: Readonly<DragInfo> | undefined; // singleton for storing the currently dragged field
  public selectedVizToken: string;

  constructor(
    private store: Store<DomainAppState>,
    private chartFacade: ChartFacade,
    private planMetadataFacade: PlanMetadataFacade
  ) {
    this.selectedVizToken = ''; // an empty chart token is handled downstream in ChartClientService
    this.chartFacade.selectedToken$.pipe(filter(isPresent)).subscribe((chartToken) => {
      this.selectedVizToken = chartToken;
    });
  }

  public updateTitle(chartToken: string, title: string) {
    this.chartFacade.updateTitleInDesigner(chartToken, title);
  }

  public updateCaption(chartToken: string, caption: ChartTypes.CaptionInfo) {
    this.chartFacade.updateCaption(chartToken, caption);
  }

  public updateBigValueDescription(chartToken: string, description: string) {
    this.chartFacade.updateBigValueDescription(chartToken, description);
  }

  public dispatchEncodingUpdate(result: EncodingResult, pillAction?: ChartTypes.PillActionType) {
    this.store.dispatch(
      ChartSwitchActions.updateDesignerChartEncoding({
        token: this.selectedVizToken,
        encoding: result.encoding,
      })
    );
  }

  public updateChartType(chartType: ChartTypes.ChartType) {
    this.chartFacade.updateChartType(this.selectedVizToken, chartType);
  }

  public updateIsAutoUpdateDisabled(disable: boolean) {
    this.chartFacade.updateIsAutoUpdateDisabled(this.selectedVizToken, disable);
  }

  public manuallyApplyChanges(): void {
    this.chartFacade.manuallyApplyChanges(this.selectedVizToken);
  }

  public addFieldAndMakeDual(
    enc: Encoding,
    field: CompiledFormula,
    channel: BasicChannel,
    leftMark: MarkType,
    rightMark: MarkType
  ) {
    this.chartFacade.addFieldAndMakeDual(this.selectedVizToken, enc, field, channel, leftMark, rightMark);
  }

  public moveFieldAndMakeDual(
    enc: Encoding,
    field: CompiledField,
    channel: BasicChannel,
    leftMark: MarkType,
    rightMark: MarkType
  ) {
    this.chartFacade.moveFieldAndMakeDual(this.selectedVizToken, enc, field, channel, leftMark, rightMark);
  }

  public removeSort(source: string) {
    this.chartFacade.removeSort(this.selectedVizToken, source);
  }

  // Chart Formatting Actions

  updateChartFormat(format: ChartTypes.ChartFormat) {
    this.chartFacade.updateChartFormat(this.selectedVizToken, format);
  }

  updateBaseFormatDisplayName(formula: string, varType: VariableType, format: ChartTypes.BaseFieldFormat) {
    this.chartFacade.updateBaseFormatDisplayName(this.selectedVizToken, formula, varType, format);
  }

  updateAxisFormat(formula: string, varType: VariableType, format: ChartTypes.AxisFieldFormat) {
    this.chartFacade.updateAxisFormat(this.selectedVizToken, formula, varType, format);
  }

  updatePaneFormat(formula: string, varType: VariableType, format: ChartTypes.PaneFieldFormat) {
    this.chartFacade.updatePaneFormat(this.selectedVizToken, formula, varType, format);
  }

  updateHeaderFormat(formula: string, varType: VariableType, format: ChartTypes.HeaderFieldFormat) {
    this.chartFacade.updateHeaderFormat(this.selectedVizToken, formula, varType, format);
  }

  updateLegendFormat(formula: string, varType: VariableType, format: ChartTypes.LegendFieldFormat) {
    this.chartFacade.updateLegendFormat(this.selectedVizToken, formula, varType, format);
  }

  updateBigValueTrendLabel(label: string) {
    this.chartFacade.updateBigValueTrendLabel(this.selectedVizToken, label);
  }

  updateTextFormat(formula: string, pane: ChartTypes.FieldPaneTypes, format: ChartTypes.TextFormat) {
    this.chartFacade.updateTextFormat(this.selectedVizToken, formula, pane, format);
  }

  updateColorField(
    formula: string,
    varType: VariableType,
    colorPaletteToken = '',
    palette: string[] = [],
    series?: string,
    map: ChartTypes.ColorEntries = {},
    gradient?: ChartTypes.ContinuousGradient,
    unassignedColor?: string,
    colorTarget?: ColorTarget
  ) {
    this.chartFacade.updateColorField(
      this.selectedVizToken,
      formula,
      varType,
      colorPaletteToken,
      palette,
      series,
      map,
      gradient,
      unassignedColor,
      colorTarget
    );
  }

  updateTheme(
    theme: string,
    colorPaletteToken = '',
    custom: string[] = [],
    showAlert = false,
    colorMap?: {
      [seriesName: string]: string;
    }
  ) {
    this.chartFacade.updateTheme(this.selectedVizToken, theme, colorPaletteToken, custom, showAlert);
  }

  updateFacetSizes(facetSizes: ChartTypes.FacetSizeMap) {
    this.chartFacade.updateFacetSizes(this.selectedVizToken, facetSizes);
  }

  public dispatchEncodingAndFormatUpdate(
    result: EncodingResult,
    facetSizes?: ChartTypes.FacetSizeMap,
    fieldFormats?: ChartTypes.FieldFormatMap,
    format?: Partial<ChartTypes.ChartFormat>,
    fieldColors?: ChartTypes.FieldColorMap,
    colorPaletteToken?: string
  ) {
    this.store.dispatch(
      ChartSwitchActions.updateEncodingAndFormat({
        token: this.selectedVizToken,
        encoding: result.encoding,
        facetSizes,
        fieldFormats,
        format,
        fieldColors,
        colorPaletteToken,
      })
    );
  }

  public dispatchReplaceChartHistory(encoding: Encoding | undefined, isAutoUpdateDisabled: boolean | undefined): void {
    this.store.dispatch(
      ChartSwitchActions.replaceChartHistory({
        token: this.selectedVizToken,
        encoding,
        isAutoUpdateDisabled,
      })
    );
  }

  clearChart() {
    this.chartFacade.clearChart(this.selectedVizToken);
  }

  clearFilters() {
    this.chartFacade.clearFilters(this.selectedVizToken);
  }

  clearSorts() {
    this.chartFacade.clearSorts(this.selectedVizToken);
  }

  clearFormatting() {
    this.chartFacade.clearFormatting(this.selectedVizToken);
  }

  undo(token: string) {
    this.chartFacade.undo(token);
  }

  redo(token: string) {
    this.chartFacade.redo(token);
  }

  swapAxes() {
    this.chartFacade.swapAxes(this.selectedVizToken);
  }

  quickSort(descending: boolean) {
    this.chartFacade.quickSort(this.selectedVizToken, descending);
  }

  drillDown(chartToken: string, target: ChartTypes.MarkTarget) {
    this.chartFacade.drillDown(chartToken, target);
  }

  changeHierarchy(chartToken: string, forward: boolean) {
    this.chartFacade.changeHierarchy(chartToken, forward);
  }

  dynamicDrillDown(
    chartToken: string,
    target: ChartTypes.MarkTarget,
    targetFieldId: string,
    drillToField: string,
    dateTruncUnit?: DateTime.Unit
  ): void {
    this.chartFacade.dynamicDrillDown(chartToken, target, targetFieldId, drillToField, dateTruncUnit);
  }

  public setOffset(offset: number) {
    this.planMetadataFacade.setOffsetInDesigner(this.selectedVizToken, offset);
  }

  // #BEGIN RESIZING
  // These resizing methods are stubbed out here to meet the requirements for the interface.
  // These should be moved out of this facade in the future but will be handled
  // in the legacy state for the time being

  minimizeChartPane() {}

  maximizeChartPane() {}

  minimizeDataPane() {}

  maximizeDataPane() {}
  // #END RESIZING

  startDrag(info: DragInfo) {
    this._dragField = info;

    if (info.field && info.source) {
      this.chartFacade.startFieldDrag(info.field, info.source);
    }
  }

  endDrag() {
    this._dragField = undefined;
    this.chartFacade.endFieldDrag();
  }

  getDataProfiling(data: DataProfilingTypes.DataProfilingChartDetails) {
    this.store.dispatch(
      ChartSwitchActions.getDataProfiling({
        token: data.token,
        cols: data.cols,
        isDesignerView: true,
        referenceId: data.referenceId,
      })
    );
  }

  getBinSize(token: string, columnName: string, isDesignerView = true) {
    this.store.dispatch(
      ChartSwitchActions.getBinSize({
        token,
        columnName,
        isDesignerView,
      })
    );
  }

  setPageSize(pageSize: number) {
    this.planMetadataFacade.setPageSizeInDesigner(this.selectedVizToken, pageSize);
  }

  get dragField() {
    return this._dragField;
  }

  get readonly(): boolean {
    return false;
  }
}
